/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { oneOfType, func, string, array, object, bool } from 'prop-types';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import p from 'polyglot/polyglot';

import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';

import styles from './tag-picker.scss';

const DropdownIndicator = props => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <div className={styles.arrow}>
          <BlackTieIcon icon="caret-up" small />
          <BlackTieIcon icon="caret-down" small />
        </div>
      </components.DropdownIndicator>
    )
  );
};

/**
 * This is the TagPicker component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class TagPicker extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    onChange: func.isRequired,
    onFocus: func,
    onBlur: func,
    value: oneOfType([array, object]),
    options: array,
    placeholder: string,
    creatable: bool,
    error: bool,
    isLoading: bool,
    isDisabled: bool,
    openMenuOnClick: bool,
  };

  static defaultProps = {
    onFocus: () => {},
    onBlur: () => {},
    value: [],
    options: [],
    placeholder: '',
    error: false,
    isLoading: false,
    isDisabled: false,
    creatable: true,
    openMenuOnClick: false,
  };

  /**
   * Render TagPicker.
   *
   * @return {Object}
   */
  render() {
    const {
      creatable,
      value,
      options,
      placeholder,
      error,
      isLoading,
      isDisabled,
      openMenuOnClick,
      onChange,
    } = this.props;

    const customStyles = {
      control: (styles, { isFocused, isSelected, isDisabled }) => ({
        ...styles,
        padding: 0,
        backgroundColor: 'white',
        borderWidth: '1px',
        borderColor: error
          ? '#FF3B53'
          : isFocused || isSelected
          ? '#000'
          : '#e8e8e8',
        boxShadow: 'none',
        borderRadius: 2,
        minHeight: 50,
        opacity: isDisabled ? 0.5 : 1,
        ':hover': {
          borderColor: isFocused || isSelected ? '#000' : '#E8E8E8',
        },
      }),
      input: styles => ({
        ...styles,
        fontSize: 18,
        color: '#4C4433',
      }),
      placeholder: styles => ({
        ...styles,
        fontSize: 18,
        color: 'rgba(140, 129, 107, 0.5)',
      }),
      menu: styles => ({
        ...styles,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
      }),
      option: (styles, { isFocused, isSelected }) => ({
        ...styles,
        cursor: 'pointer',
        color: '#4C4433',
        backgroundColor: isFocused || isSelected ? '#E2DDD7' : 'white',
        ':hover': {
          backgroundColor: '#E2DDD7',
        },
      }),
      multiValue: styles => ({
        ...styles,
        color: 'white',
        backgroundColor: '#8C816B',
        padding: 3,
        borderRadius: 2,
      }),
      multiValueLabel: styles => ({
        ...styles,
        color: 'white',
      }),
      multiValueRemove: styles => ({
        ...styles,
        cursor: 'pointer',
        ':hover': {
          color: 'white',
        },
      }),
      clearIndicator: () => ({
        display: 'none',
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
    };

    const settings = {
      value,
      options,
      placeholder,
      isDisabled,
      isLoading,
      openMenuOnClick,
      isMulti: true,
      onChange: onChange,
      styles: customStyles,
      menuShouldScrollIntoView: false,
      formatCreateLabel: value =>
        p.t('search.form.filter.create_new', { value }),
      classNamePrefix: 'react-select',
      components: {
        DropdownIndicator,
      },
    };

    if (creatable) {
      return <CreatableSelect {...settings} />;
    }

    return <Select {...settings} />;
  }
}
